import React, { useEffect, useState } from "react";
import {
  Modal,
  View,
  Text,
  FlatList,
  Pressable,
  StyleSheet,
  TextInput,
  Button,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import CommonService, { serviceResponse } from "../../../services/common";
import { getResources, Resource } from "../../../services/Resource";
import { RootState } from "../../../services/store/app";
import { setLocalResources } from "../../../services/store/common";
import { CloseIcon } from "../../Icons";
import { colors } from "../../Shared";

interface Props {
  isVisible: boolean;
  transparent?: boolean;
  onSelect: (resource: Resource) => void;
  onClose: () => void;
}

const commonService = new CommonService();

export default function ResourceSelector({
  transparent = true,
  isVisible,
  onSelect,
  onClose,
}: Props) {
  const dispatch = useDispatch();
  const commonStore = useSelector((state: RootState) => state.commonReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [resources, setResources] = useState<Resource[]>([]);
  const [resource, setResource] = useState<Resource | null>(null);

  useEffect(() => {
    setResources(commonStore.resources);
  }, [commonStore]);

  const unsubscribe = async () => {
    const req = await getResources();

    if (req === serviceResponse.systemError) {
      return alert("Something went wrong, please try it again reloading");
    }

    if (req === serviceResponse.notContent) {
      return dispatch(setLocalResources([]));
    }

    const data = req as Resource[];
    dispatch(setLocalResources(data));
  };

  useEffect(() => {
    unsubscribe();
  }, []);

  const filter = ({ isActive, name, positionName, fileName }: Resource) => {
    if (!isActive) {
      return false;
    }

    if (
      commonService
        .replaceSpecialChars(name.toLowerCase())
        .match(search.toLowerCase()) ||
      commonService
        .replaceSpecialChars(positionName.toLowerCase())
        .match(search.toLowerCase()) ||
      commonService
        .replaceSpecialChars(fileName.toLowerCase())
        .match(search.toLowerCase())
    ) {
      return true;
    }

    return false;
  };

  const onSetResource = (resourceToSet: Resource) => {
    if (resource?.id === resourceToSet.id) {
      return setResource(null);
    }

    return setResource(resourceToSet);
  };

  const renderItems = () => {
    if (!resources.length) {
      return (
        <View style={{ flex: 1, alignItems: "center", marginTop: 10 }}>
          <Text>Without resources to show</Text>
        </View>
      );
    }

    return (
      <FlatList
        data={resources.filter(filter)}
        style={{ flex: 1, padding: 5 }}
        ListEmptyComponent={
          <View style={{ flex: 1, alignItems: "center", marginTop: 10 }}>
            <Text>Without results</Text>
          </View>
        }
        renderItem={({ item }) => (
          <Pressable
            onPress={() => onSetResource(item)}
            style={[
              styles.itemContainer,
              {
                backgroundColor:
                  resource?.id === item.id ? colors.bg.medium : colors.bg.light,
              },
            ]}
            key={item.id}
          >
            {/* <Text style={{ textTransform: "capitalize" }}>{item.name}</Text> */}
            <Text
              style={{
                textTransform: "capitalize",
                fontWeight: "300",
                fontSize: 16,
              }}
            >
              {item.name}
            </Text>
            <Text
              style={{
                marginTop: 7,
                textTransform: "capitalize",
                fontWeight: "500",
                fontSize: 13,
              }}
            >
              {item.positionName}
            </Text>
          </Pressable>
        )}
      />
    );
  };

  const next = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    if (!resource) {
      return;
    }

    onSelect(resource);
    onClose();
    setIsLoading(false);
  };

  return (
    <Modal transparent={transparent} visible={isVisible}>
      <View style={styles.popUp}>
        <View style={styles.container}>
          <View style={styles.headerContainer}>
            <TextInput
              value={search}
              onChangeText={setSearch}
              style={styles.headerInput}
              placeholder="search"
            />

            {search.length > 0 && (
              <Pressable style={styles.headerIcon} onPress={()=>setSearch("")}>
                <CloseIcon size={15} />
              </Pressable>
            )}
          </View>

          {renderItems()}

          {resources.length > 0 && (
            <View style={styles.footerContainer}>
              <Button title="cancel" onPress={onClose} />
              <View style={{ height: 5 }} />
              <Button
                title="continue"
                onPress={next}
                color={colors.bg.primary}
                disabled={resource === null}
              />
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  popUp: {
    backgroundColor: "#00000030",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  container: {
    width: 400,
    height: "70%",
    backgroundColor: colors.bg.white,
    borderRadius: 5,
  },
  headerContainer: {
    backgroundColor: colors.bg.light,
    height: 50,
    flexDirection: "row",
    padding: 5,
    borderTopRightRadius: 5,
    borderTopLeftRadius: 5,
  },
  headerIcon: {
    position: "absolute",
    top: 17.5,
    right: 15,
  },
  headerInput: {
    flex: 1,
    backgroundColor: colors.bg.white,
    paddingLeft: 10,
    borderRadius: 5,
  },
  footerContainer: {
    padding: 5,
  },
  footerButton: {
    width: 192.5,
    height: 60,
    borderRadius: 5,
    justifyContent: "center",
    alignItems: "center",
  },
  itemContainer: {
    justifyContent: "center",
    // height: 50,
    borderRadius: 5,
    marginTop: 5,
    padding: 10,
  },
});
